import type { CompletionRuleContract } from '../api/generated/model/completionRuleContract';

export type CompletionRule = CompletionRuleContract;

const getValue = (values: Record<string, unknown>, path: string): unknown =>
  path.split('.').reduce<unknown>((current, segment) => {
    if (current === null || typeof current !== 'object') return undefined;
    return (current as Record<string, unknown>)[segment];
  }, values);

const hasValue = (value: unknown): boolean =>
  value !== null &&
  value !== undefined &&
  (typeof value !== 'string' || value.trim().length > 0);

export const isComplete = (
  rule: CompletionRule,
  values: Record<string, unknown>,
): boolean => {
  if (rule.required != null) {
    return hasValue(getValue(values, rule.required));
  }

  if (rule.present != null) {
    const value = getValue(values, rule.present);
    return value !== null && value !== undefined;
  }

  if (rule.checked != null) {
    return getValue(values, rule.checked) === true;
  }

  if (rule.nonZero != null) {
    const value = getValue(values, rule.nonZero);
    return typeof value === 'number' && value !== 0;
  }

  if (rule.initializedDate != null) {
    const value = getValue(values, rule.initializedDate);
    if (value === null || value === undefined || value === '') return false;
    const date = value instanceof Date ? value : new Date(value as string);
    return !Number.isNaN(date.getTime()) && date.getUTCFullYear() > 1;
  }

  if (rule.exactlyOne != null) {
    return rule.exactlyOne.filter((field) => getValue(values, field) === true).length === 1;
  }

  if (rule.all != null) {
    return rule.all.every((childRule) => isComplete(childRule, values));
  }

  if (rule.any != null) {
    return rule.any.some((childRule) => isComplete(childRule, values));
  }

  if (rule.every == null) return false;

  const everyRule = rule.every;
  const collection = getValue(values, everyRule.field);
  if (collection === null || collection === undefined) return !everyRule.requireAny;
  if (!Array.isArray(collection)) return false;
  return (!everyRule.requireAny || collection.length > 0) &&
    collection.every((item) =>
      item !== null && typeof item === 'object' &&
      isComplete(everyRule.rule, item as Record<string, unknown>));
};

export const missingFields = (
  rule: CompletionRule,
  values: Record<string, unknown>,
): string[] => {
  if (isComplete(rule, values)) return [];

  if (rule.required != null) return [rule.required];
  if (rule.present != null) return [rule.present];
  if (rule.checked != null) return [rule.checked];
  if (rule.nonZero != null) return [rule.nonZero];
  if (rule.initializedDate != null) return [rule.initializedDate];
  if (rule.exactlyOne != null) return rule.exactlyOne;

  if (rule.all != null) {
    return rule.all.flatMap((childRule) => missingFields(childRule, values));
  }

  if (rule.any != null) {
    return rule.any.flatMap((childRule) => missingFields(childRule, values));
  }

  if (rule.every != null) {
    return [rule.every.field];
  }

  return [];
};
